import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import InsightsIcon from '@mui/icons-material/Insights';
import PersonIcon from '@mui/icons-material/Person';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Box, Chip, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { type RawTimeZone, rawTimeZones } from '@vvo/tzdb';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useMonitoringHoursRules, usePatient } from '../../api/index.js';
import { useCheckPermissions } from '../../auth/index.js';
import { ActivityEventsTable } from '../../components/Activity/index.js';
import { EmergencyContactsTable } from '../../components/EmergencyContacts/index.js';
import { type PatientFormValues } from "../../components/Forms/formTypes.js";
import {
  PatientActivityConfigForm,
  PatientCamerasTable,
  PatientInformationForm,
  PatientMediaProviderForm,
} from '../../components/Patients/index.js';
import PatientActivityUsage from '../../components/Patients/PatientActivityUsage.js';
import PatientNvrsTable from '../../components/Patients/PatientNvrsTable.js';
import {
  SettingsCard,
  type SettingsSection,
  SettingsSectionNav,
  SetupChecklist,
  type SetupStep,
} from '../../components/Settings/index.js';
import useActiveSection from '../../hooks/use-active-section.js';
import useSnackBar from '../../hooks/use-snack-bar.js';
import { track, Events, useMutation } from '../../utils/index.js';
import MonitoringHours from '../MonitoringHours.js';

const SECTION_DETAILS = 'patient-settings-details';
const SECTION_SCHEDULE = 'patient-settings-schedule';
const SECTION_CONTACTS = 'patient-settings-contacts';
const SECTION_DEVICES = 'patient-settings-devices';
const SECTION_AI = 'patient-settings-ai';
const SECTION_ACTIVITY = 'patient-settings-activity';

const PatientUpdate = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<PatientFormValues>();
  const [dirtySections, setDirtySections] = useState<Record<string, boolean>>({});
  const { isLoading, ...patient } = usePatient(
    patientId,
    {
      includeCameras: true,
      includeEmergencyContacts: true,
    },
    {
      onSuccess: (data) => {
        const selectedTimeZone = rawTimeZones.find((timeZone) => {
          return timeZone.name === data?.timeZone;
        });
        setFormData({
          firstName: data.firstName,
          lastName: data.lastName,
          physicalAddress: data.physicalAddress,
          timeZone: selectedTimeZone as RawTimeZone,
          careInstructions: data.careInstructions || '',
          active: data.active,
        });
      },
    },
  );
  const snackBar = useSnackBar();

  const {
    canReadEmergencyContact,
    canReadCamera,
    canReadMonitoringHours,
    canUpdateActivityConfig,
    canReadActivityEvent,
    canUpdateMediaProvider,
  } = useCheckPermissions();

  const mutation = useMutation({
    path: `/patients/${patientId}`,
    method: 'PUT',
    onSuccess() {
      snackBar.success('Patient\'s personal information updated successfully!');

      track(Events.PatientUpdate, {
        patientId,
      });
    },
  });

  async function onSubmit(values: PatientFormValues, callback: () => void) {
    const { timeZone } = values;
    mutation.mutate(
      {
        ...values,
        timeZone: timeZone?.name,
      },
      {
        onSuccess: () => {
          patient.refetch();
          callback();
        },
      },
    );
  }

  const setSectionDirty = useCallback((sectionId: string, isDirty: boolean) => {
    setDirtySections((current) => (current[sectionId] === isDirty ? current : { ...current, [sectionId]: isDirty }));
  }, []);

  const onDetailsDirtyChange = useCallback(
    (isDirty: boolean) => setSectionDirty('details', isDirty),
    [setSectionDirty],
  );
  const onActivityConfigDirtyChange = useCallback(
    (isDirty: boolean) => setSectionDirty('activityConfig', isDirty),
    [setSectionDirty],
  );
  const onMediaProviderDirtyChange = useCallback(
    (isDirty: boolean) => setSectionDirty('mediaProvider', isDirty),
    [setSectionDirty],
  );

  const showAiSection = (canUpdateActivityConfig || canUpdateMediaProvider) && !!patientId && !!patient?.data;
  const isAiSectionDirty = !!dirtySections.activityConfig || !!dirtySections.mediaProvider;

  const sections: SettingsSection[] = useMemo(() => {
    const available: SettingsSection[] = [
      { id: SECTION_DETAILS, label: 'Details', icon: <PersonIcon fontSize={'small'} />, isDirty: dirtySections.details },
    ];
    if (canReadMonitoringHours) {
      available.push({ id: SECTION_SCHEDULE, label: 'Schedule', icon: <CalendarMonthIcon fontSize={'small'} /> });
    }
    if (canReadEmergencyContact) {
      available.push({ id: SECTION_CONTACTS, label: 'Contacts', icon: <ContactPhoneIcon fontSize={'small'} /> });
    }
    if (canReadCamera) {
      available.push({ id: SECTION_DEVICES, label: 'Devices', icon: <VideocamIcon fontSize={'small'} /> });
    }
    if (showAiSection) {
      available.push({
        id: SECTION_AI,
        label: 'AI & Streaming',
        icon: <AutoAwesomeIcon fontSize={'small'} />,
        isDirty: isAiSectionDirty,
      });
    }
    if (canReadActivityEvent) {
      available.push({ id: SECTION_ACTIVITY, label: 'Activity', icon: <InsightsIcon fontSize={'small'} /> });
    }
    return available;
  }, [
    canReadMonitoringHours,
    canReadEmergencyContact,
    canReadCamera,
    canReadActivityEvent,
    showAiSection,
    isAiSectionDirty,
    dirtySections.details,
  ]);

  const sectionIds = useMemo(() => sections.map((section) => section.id), [sections]);
  const activeSection = useActiveSection(sectionIds);

  const onSelectSection = useCallback((sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const monitoringHoursRules = useMonitoringHoursRules(patientId, { enabled: canReadMonitoringHours });

  const visibleSectionIds = useMemo(() => new Set(sections.map((section) => section.id)), [sections]);

  const setupSteps: SetupStep[] = useMemo(
    () =>
      [
      { sectionId: SECTION_DETAILS, label: 'Details', isComplete: true },
      {
        sectionId: SECTION_SCHEDULE,
        label: 'Schedule',
        isComplete: (monitoringHoursRules.data?.length ?? 0) > 0,
      },
      {
        sectionId: SECTION_CONTACTS,
        label: 'Contacts',
        isComplete: (patient?.data?.emergencyContacts?.length ?? 0) > 0,
      },
      {
        sectionId: SECTION_DEVICES,
        label: 'Devices',
        isComplete: (patient?.data?.cameras?.length ?? 0) > 0,
      },
      {
        sectionId: SECTION_AI,
        label: 'AI monitoring',
        isComplete: !!patient?.data?.activityRecognitionEnabled,
        isOptional: true,
      },
      ].filter((step) => visibleSectionIds.has(step.sectionId)),
    [
      monitoringHoursRules.data,
      patient?.data?.emergencyContacts,
      patient?.data?.cameras,
      patient?.data?.activityRecognitionEnabled,
      visibleSectionIds,
    ],
  );

  const patientName = patient?.data ? `${patient.data.firstName} ${patient.data.lastName}` : '';

  return (
    <Box>
      <Stack direction={'row'} alignItems={'center'} spacing={1.5}>
        <Tooltip arrow title={'Back to patient'}>
          <IconButton aria-label={'Back to patient'} onClick={() => navigate(`/patients/${patientId}`)}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant={'h2'} component={'h1'}>
            Patient settings
          </Typography>
          <Typography variant={'body2'} color={'text.secondary'}>
            {patientName}
            {patient?.data?.timeZone ? ` · ${patient.data.timeZone}` : ''}
          </Typography>
        </Box>
        {patient?.data ? (
          <Chip
            size={'small'}
            sx={{ ml: 'auto' }}
            color={patient.data.active ? 'success' : 'default'}
            variant={patient.data.active ? 'filled' : 'outlined'}
            label={patient.data.active ? 'Active' : 'Inactive'}
          />
        ) : null}
      </Stack>

      {patient?.data ? (
        <SetupChecklist
          patientFirstName={patient.data.firstName}
          steps={setupSteps}
          onSelectSection={onSelectSection}
        />
      ) : null}

      <Stack direction={'row'} spacing={4} alignItems={'flex-start'} sx={{ mt: 3 }}>
        <SettingsSectionNav sections={sections} activeSection={activeSection} onSelect={onSelectSection} />

        <Stack spacing={3} sx={{ flexGrow: 1, minWidth: 0 }}>
          <SettingsCard
            id={SECTION_DETAILS}
            icon={<PersonIcon color={'primary'} />}
            title={'Details'}
            subtitle={'Personal information and care instructions.'}
            isDirty={dirtySections.details}
          >
            <PatientInformationForm values={formData} onSubmit={onSubmit} onDirtyChange={onDetailsDirtyChange} />
          </SettingsCard>

          {canReadMonitoringHours ? (
            <SettingsCard
              id={SECTION_SCHEDULE}
              icon={<CalendarMonthIcon color={'primary'} />}
              title={'Schedule'}
              subtitle={"Monitoring hours and check-in rules, in the patient's timezone."}
            >
              <MonitoringHours patientId={patientId} />
            </SettingsCard>
          ) : null}

          {canReadEmergencyContact && patientId ? (
            <SettingsCard
              id={SECTION_CONTACTS}
              icon={<ContactPhoneIcon color={'primary'} />}
              title={'Contacts'}
              subtitle={'Emergency contacts, called in priority order.'}
            >
              <EmergencyContactsTable
                data={patient?.data?.emergencyContacts}
                patientId={patientId}
                refetch={patient.refetch}
                isLoading={isLoading}
              />
            </SettingsCard>
          ) : null}

          {canReadCamera ? (
            <SettingsCard
              id={SECTION_DEVICES}
              icon={<VideocamIcon color={'primary'} />}
              title={'Devices'}
              subtitle={"Cameras and NVRs in the patient's home."}
            >
              <Stack spacing={4}>
                <PatientCamerasTable patientId={patientId} title={'Cameras'} />
                <PatientNvrsTable patientId={patientId} title={'NVRs'} />
              </Stack>
            </SettingsCard>
          ) : null}

          {showAiSection && patientId && patient?.data ? (
            <SettingsCard
              id={SECTION_AI}
              icon={<AutoAwesomeIcon color={'primary'} />}
              title={'AI & Streaming'}
              subtitle={'Automated check-in captures and the media server for this patient.'}
              isDirty={isAiSectionDirty}
            >
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={4} alignItems={'flex-start'}>
                <Stack spacing={3} sx={{ width: { xs: '100%', lg: 520 }, flexShrink: 0 }}>
                  {canUpdateMediaProvider ? (
                    <PatientMediaProviderForm
                      patientId={patientId}
                      mediaProvider={patient.data.mediaProvider}
                      canUpdate={canUpdateMediaProvider}
                      onDirtyChange={onMediaProviderDirtyChange}
                    />
                  ) : null}

                  {canUpdateActivityConfig && canUpdateMediaProvider ? <Divider /> : null}

                  {canUpdateActivityConfig ? (
                    <PatientActivityConfigForm
                      patientId={patientId}
                      enabled={!!patient.data.activityRecognitionEnabled}
                      intervalMinutes={patient.data.activityCaptureIntervalMinutes ?? null}
                      clipDurationSeconds={patient.data.activityClipDurationSeconds ?? null}
                      aiContext={patient.data.activityContext ?? null}
                      dailyCostCapUsd={patient.data.activityDailyCostCapUsd ?? null}
                      modelId={patient.data.activityModelId ?? null}
                      classifyStrategy={patient.data.activityClassifyStrategy ?? null}
                      mergeLayout={patient.data.activityMergeLayout ?? null}
                      canUpdate={canUpdateActivityConfig}
                      onDirtyChange={onActivityConfigDirtyChange}
                    />
                  ) : null}
                </Stack>

                {canUpdateActivityConfig ? (
                  <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
                    <PatientActivityUsage patientId={patientId} />
                  </Box>
                ) : null}
              </Stack>
            </SettingsCard>
          ) : null}

          {canReadActivityEvent && patientId ? (
            <SettingsCard
              id={SECTION_ACTIVITY}
              icon={<InsightsIcon color={'primary'} />}
              title={'Activity'}
              subtitle={'Recent AI check-in events.'}
            >
              <ActivityEventsTable patientId={patientId} />
            </SettingsCard>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
};

export default PatientUpdate;
